import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./css/MatchLengthAndFormation.css";
import Pitch from "./Pitch"; // Import the Pitch component
import config from "./config";
import axios from "axios";

const API_URL =
  "https://lykns00a87.execute-api.us-east-2.amazonaws.com/default/football_app_data_crud";

const devLog = (...args) => {
  if (config.IS_DEV) {
    console.log(...args);
  }
};

const MatchLengthAndFormation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    players = [],
    matchFolderName = null,
    myTeam = "",
    opposition = "",
    userId = "",
    selectedTeamId = "",
    matchId = null,
    matchFolder = null,
    matchDate = null,
    status = "",
    otherInfo = "",
    homeAway = "",
    initialTeamScores = {},
    ...rest
  } = location.state || {};

  const [selectedTeam, setSelectedTeam] = useState(selectedTeamId || "");
  const [preferencesVisible, setPreferencesVisible] = useState(false);
  const [error, setError] = useState("");
  const [showAssignments, setShowAssignments] = useState(false);

  // Ensure all players have a unique player_id
  const [playersWithIds] = useState(
    players.map((player, index) => ({
      ...player,
      player_id: player.SK || `player-${index}`,
    }))
  );

  // Scroll to the top when the component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  devLog("Data received in MatchLengthAndFormation:", {
    players,
    matchFolderName,
    myTeam,
    opposition,
    userId,
    selectedTeamId,
    matchId,
    matchFolder,
    matchDate,
    status,
    otherInfo,
    homeAway,
    initialTeamScores,
  });

  // Dropdown color options
  const colorOptions = [
    { name: "Red", value: "#FF4C4C" },
    { name: "Blue", value: "#4C6FFF" },
    { name: "Green", value: "#4CFF4C" },
    { name: "Purple", value: "#B84CFF" },
    { name: "Orange", value: "#FF944C" },
    { name: "Yellow", value: "#FFD700" },
  ];

  // States for match preferences
  const [markerColor, setMarkerColor] = useState(colorOptions[0].value);
  const [gkColor, setGkColor] = useState(() => {
    // Find a color different from the initial markerColor
    const initialGkColor = colorOptions.find(
      (color) => color.value !== colorOptions[0].value
    ).value;
    return initialGkColor;
  });

  const [matchType, setMatchType] = useState("5");
  const [matchLength, setMatchLength] = useState("10");
  const [numberOfParts, setNumberOfParts] = useState("2");
  const [formation, setFormation] = useState("");

  // This holds which player is assigned to each position index: { 0: playerId, 1: playerId, ... }
  const [positionAssignments, setPositionAssignments] = useState({});

  // This holds the bench/home statuses for unassigned players
  const [playerStatuses, setPlayerStatuses] = useState(
    playersWithIds.reduce((acc, player) => {
      acc[player.player_id] = { status: null };
      return acc;
    }, {})
  );

  // Function to fetch team preferences (optional, from your code)
  const fetchTeamPreferences = async (teamId) => {
    try {
      const requestPayload = {
        operationType: "fetch_team_preference",
        data: { team_id: teamId },
      };

      const response = await axios.post(API_URL, requestPayload);
      const preferences = response.data.body
        ? JSON.parse(response.data.body).preferences
        : {};

      devLog("Fetched team preferences:", preferences);

      // Update states based on fetched preferences
      setMatchType(preferences.matchType || "5");
      setMatchLength(preferences.matchLength || "10");
      setNumberOfParts(preferences.numberOfParts || "2");
      setMarkerColor(preferences.markerColor || colorOptions[0].value);
    } catch (error) {
      console.error("Error fetching team preferences:", error.message);
      setError("Failed to load team preferences. Please try again.");
    }
  };

  useEffect(() => {
    if (selectedTeam) {
      fetchTeamPreferences(selectedTeam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam]);

  // Handle changes in top dropdowns
  const handleMarkerColorChange = (e) => {
    const newMarkerColor = e.target.value;
    setMarkerColor(newMarkerColor);

    // Automatically select a GK color that doesn't conflict
    const newGkColor = colorOptions.find(
      (color) => color.value !== newMarkerColor
    ).value;
    setGkColor(newGkColor);
  };

  const handleGkColorChange = (e) => {
    setGkColor(e.target.value);
  };

  const matchLengths = ["10", "12.5", "15", "20", "25", "30", "35", "40", "45"];
  const matchTypes = ["5", "6", "7", "9", "11"];

  const formationsByMatchType = {
    5: [
      {
        name: "1-2-1",
        positions: ["GK", "DEF", "MID-L", "MID-R", "FWD"],
        coordinates: [
          { top: 85, left: 50 }, // GK
          { top: 60, left: 50 }, // DEF
          { top: 37, left: 30 }, // MID-L
          { top: 37, left: 70 }, // MID-R
          { top: 15, left: 50 }, // FWD
        ],
      },
      {
        name: "2-1-1",
        positions: ["GK", "DEF-L", "DEF-R", "MID", "FWD"],
        coordinates: [
          { top: 85, left: 50 }, // GK
          { top: 60, left: 30 }, // DEF-L
          { top: 60, left: 70 }, // DEF-R
          { top: 37, left: 50 }, // MID
          { top: 15, left: 50 }, // FWD
        ],
      },
    ],
    6: [
      {
        name: "2-1-2",
        positions: ["GK", "DEF-L", "DEF-R", "MID", "FWD-L", "FWD-R"],
        coordinates: [
          { top: 85, left: 50 }, // GK
          { top: 60, left: 35 }, // DEF-L
          { top: 60, left: 65 }, // DEF-R
          { top: 45, left: 50 }, // MID
          { top: 25, left: 30 }, // FWD-L
          { top: 25, left: 70 }, // FWD-R
        ],
      },
      {
        name: "2-2-1",
        positions: ["GK", "DEF-L", "DEF-R", "MID-L", "MID-R", "FWD"],
        coordinates: [
          { top: 85, left: 50 }, // GK
          { top: 60, left: 40 }, // DEF-L
          { top: 60, left: 60 }, // DEF-R
          { top: 40, left: 30 }, // MID-L
          { top: 40, left: 70 }, // MID-R
          { top: 25, left: 50 }, // FWD
        ],
      },
      {
        name: "3-1-1",
        positions: ["GK", "DEF-L", "DEF-C", "DEF-R", "MID", "FWD"],
        coordinates: [
          { top: 85, left: 50 }, // GK
          { top: 63, left: 30 }, // DEF-L
          { top: 63, left: 50 }, // DEF-C
          { top: 63, left: 70 }, // DEF-R
          { top: 45, left: 50 }, // MID
          { top: 25, left: 50 }, // FWD
        ],
      },
    ],
    7: [
      {
        name: "2-3-1",
        positions: ["GK", "DEF-L", "DEF-R", "MID-L", "MID-C", "MID-R", "FWD"],
        coordinates: [
          { top: 85, left: 50 }, // GK
          { top: 60, left: 35 }, // DEF-L
          { top: 60, left: 65 }, // DEF-R
          { top: 37, left: 25 }, // MID-L
          { top: 37, left: 50 }, // MID-C
          { top: 37, left: 75 }, // MID-R
          { top: 15, left: 50 }, // FWD
        ],
      },
      {
        name: "3-2-1",
        positions: ["GK", "DEF-L", "DEF-C", "DEF-R", "MID-L", "MID-R", "FWD"],
        coordinates: [
          { top: 85, left: 50 }, // GK
          { top: 60, left: 25 }, // DEF-L
          { top: 60, left: 50 }, // DEF-C
          { top: 60, left: 75 }, // DEF-R
          { top: 37, left: 40 }, // MID-L
          { top: 37, left: 60 }, // MID-R
          { top: 15, left: 50 }, // FWD
        ],
      },
    ],
    9: [
      {
        name: "3-3-2",
        positions: [
          "GK",
          "CB-L",
          "CB-C",
          "CB-R",
          "MID-L",
          "MID-C",
          "MID-R",
          "FWD-L",
          "FWD-R",
        ],
        coordinates: [
          { top: 85, left: 50 }, // GK
          { top: 60, left: 30 }, // CB-L
          { top: 60, left: 50 }, // CB-C
          { top: 60, left: 70 }, // CB-R
          { top: 37, left: 20 }, // MID-L
          { top: 37, left: 50 }, // MID-C
          { top: 37, left: 80 }, // MID-R
          { top: 15, left: 40 }, // FWD-L
          { top: 15, left: 60 }, // FWD-R
        ],
      },
      {
        name: "4-3-1",
        positions: [
          "GK",
          "LB",
          "CB-L",
          "CB-R",
          "RB",
          "MID-L",
          "MID-C",
          "MID-R",
          "FWD",
        ],
        coordinates: [
          { top: 85, left: 50 }, // GK
          { top: 60, left: 20 }, // LB
          { top: 60, left: 40 }, // CB-L
          { top: 60, left: 60 }, // CB-R
          { top: 60, left: 80 }, // RB
          { top: 37, left: 30 }, // MID-L
          { top: 37, left: 50 }, // MID-C
          { top: 37, left: 70 }, // MID-R
          { top: 15, left: 50 }, // FWD
        ],
      },
      {
        name: "3-1-3-1",
        positions: [
          "GK",
          "CB-L",
          "CB-C",
          "CB-R",
          "CDM",
          "MID-L",
          "MID-C",
          "MID-R",
          "FWD",
        ],
        coordinates: [
          { top: 88, left: 50 }, // GK
          { top: 71, left: 30 }, // CB-L
          { top: 71, left: 50 }, // CB-C
          { top: 71, left: 70 }, // CB-R
          { top: 52, left: 50 }, // CB-R
          { top: 32, left: 30 }, // MID-L
          { top: 32, left: 50 }, // MID-C
          { top: 32, left: 70 }, // MID-R
          { top: 13, left: 50 }, // FWD
        ],
      },
      {
        name: "3-4-1",
        positions: [
          "GK",
          "CB-L",
          "CB-C",
          "CB-R",
          "MID-L",
          "MID-C",
          "MID-C",
          "MID-R",
          "FWD",
        ],
        coordinates: [
          { top: 85, left: 50 }, // GK
          { top: 60, left: 30 }, // CB-L
          { top: 60, left: 50 }, // CB-C
          { top: 60, left: 70 }, // CB-R
          { top: 37, left: 20 }, // MID-L
          { top: 37, left: 40 }, // MID-C
          { top: 37, left: 60 }, // MID-C
          { top: 37, left: 80 }, // MID-R
          { top: 15, left: 50 }, // FWD
        ],
      },
    ],
    11: [
      {
        name: "4-4-2",
        positions: [
          "GK",
          "LB",
          "CB-L",
          "CB-R",
          "RB",
          "LM",
          "CM-L",
          "CM-R",
          "RM",
          "ST-L",
          "ST-R",
        ],
        coordinates: [
          { top: 85, left: 50 }, // GK
          { top: 60, left: 20 }, // LB
          { top: 60, left: 40 }, // CB-L
          { top: 60, left: 60 }, // CB-R
          { top: 60, left: 80 }, // RB
          { top: 37, left: 15 }, // LM
          { top: 37, left: 40 }, // CM-L
          { top: 37, left: 60 }, // CM-R
          { top: 37, left: 85 }, // RM
          { top: 15, left: 40 }, // ST-L
          { top: 15, left: 60 }, // ST-R
        ],
      },
      {
        name: "4-3-3",
        positions: [
          "GK",
          "LB",
          "CB-L",
          "CB-R",
          "RB",
          "CM-L",
          "CM-C",
          "CM-R",
          "LW",
          "ST",
          "RW",
        ],
        coordinates: [
          { top: 85, left: 50 }, // GK
          { top: 63, left: 20 }, // LB
          { top: 63, left: 40 }, // CB-L
          { top: 63, left: 60 }, // CB-R
          { top: 63, left: 80 }, // RB
          { top: 43, left: 30 }, // CM-L
          { top: 43, left: 50 }, // CM-C
          { top: 43, left: 70 }, // CM-R
          { top: 25, left: 20 }, // LW
          { top: 10, left: 50 }, // ST
          { top: 25, left: 80 }, // RW
        ],
      },
      {
        name: "3-5-2",
        positions: [
          "GK",
          "CB-L",
          "CB-C",
          "CB-R",
          "LWB",
          "CM-L",
          "CM-C",
          "CM-R",
          "RWB",
          "ST-L",
          "ST-R",
        ],
        coordinates: [
          { top: 85, left: 50 }, // GK
          { top: 60, left: 30 }, // CB-L
          { top: 60, left: 50 }, // CB-C
          { top: 60, left: 70 }, // CB-R
          { top: 45, left: 15 }, // LWB
          { top: 32, left: 35 }, // CM-L
          { top: 32, left: 50 }, // CM-C
          { top: 32, left: 65 }, // CM-R
          { top: 45, left: 85 }, // RWB
          { top: 10, left: 40 }, // ST-L
          { top: 10, left: 60 }, // ST-R
        ],
      },
      {
        name: "4-2-3-1",
        positions: [
          "GK",
          "LB",
          "CB-L",
          "CB-R",
          "RB",
          "CDM-L",
          "CDM-R",
          "LW",
          "CAM",
          "RW",
          "ST",
        ],
        coordinates: [
          { top: 85, left: 50 }, // GK
          { top: 67, left: 20 }, // LB
          { top: 67, left: 40 }, // CB-L
          { top: 67, left: 60 }, // CB-R
          { top: 67, left: 80 }, // RB
          { top: 50, left: 40 }, // CDM-L
          { top: 50, left: 60 }, // CDM-R
          { top: 32, left: 20 }, // LW
          { top: 32, left: 50 }, // CAM
          { top: 32, left: 80 }, // RW
          { top: 15, left: 50 }, // ST
        ],
      },
    ],
  };

  // When matchType changes, reset formation and positions
  const handleMatchTypeChange = (e) => {
    const newMatchType = e.target.value;
    setMatchType(newMatchType);
    setFormation("");
    setPositionAssignments({});
  };

  const handleMatchLengthChange = (e) => {
    setMatchLength(e.target.value);
  };

  const handleNumberOfPartsChange = (e) => {
    setNumberOfParts(e.target.value);
  };

  const handleFormationChange = (selectedFormation) => {
    setFormation(selectedFormation);
  };

  // -- Let the pitch or the dropdown call this same function to update assignment
  const handlePlayerAssignment = (positionIndex, playerId) => {
    setPositionAssignments((prevAssignments) => ({
      ...prevAssignments,
      [positionIndex]: playerId,
    }));
  };

  // Which positions exist in the selected formation
  const formationOptions = formationsByMatchType[matchType] || [];
  const selectedFormationObj = formationOptions.find(
    (f) => f.name === formation
  );
  const formationPositions = selectedFormationObj
    ? selectedFormationObj.positions
    : [];
  const formationCoordinates = selectedFormationObj
    ? selectedFormationObj.coordinates
    : [];

  // Figure out which players are assigned
  const assignedPlayers = Object.values(positionAssignments).filter(Boolean);

  // Handle bench/home status for unassigned players
  const handleStatusChange = (playerId, status) => {
    setPlayerStatuses((prevStatuses) => ({
      ...prevStatuses,
      [playerId]: { status },
    }));
  };

  // Check if all positions are filled
  const allPositionsFilled =
    formationPositions.length > 0 &&
    formationPositions.every(
      (_, index) =>
        positionAssignments[index] && positionAssignments[index] !== ""
    );

  const isValid = formation && allPositionsFilled;

  // Handling the Continue button
  const handleContinue = async () => {
    if (!isValid) {
      alert("Please fill all positions and select a formation.");
      return;
    }
    // Create arrays for assigned/unassigned players
    const assignedPlayersSelections = Object.entries(positionAssignments).map(
      ([positionIndex, playerId]) => {
        const player = playersWithIds.find((p) => p.player_id === playerId);
        return {
          ...player,
          position: formationPositions[positionIndex],
          status: "playing",
        };
      }
    );

    const unassignedPlayers = playersWithIds.filter(
      (player) => !assignedPlayers.includes(player.player_id)
    );

    const unassignedPlayerSelections = unassignedPlayers.map((player) => ({
      ...player,
      position: playerStatuses[player.player_id]?.status || "bench",
      status:
        playerStatuses[player.player_id]?.status === "home" ? "home" : "sub",
    }));

    // Combine both arrays
    const playerSelections = [
      ...assignedPlayersSelections,
      ...unassignedPlayerSelections,
    ];

    // Filter out players with status "home"
    const filteredPlayerSelections = playerSelections.filter(
      (player) => player.status !== "home"
    );

    const matchInfo = {
      myTeam,
      opposition,
      homeAway,
      players: filteredPlayerSelections, // use the filtered array here
      otherInfo,
      matchId,
      match_folder: matchFolderName,
      match_date: matchDate || new Date().toISOString(),
      status: "ongoing",
      teamScores: initialTeamScores,
    };

    devLog("Formatted match info for saving:", matchInfo);

    try {
      await saveMatchInfoToS3(matchFolderName, matchInfo);
      devLog("Match info successfully saved to S3");

      sessionStorage.setItem("accessedViaNavigation", "true");
      navigate("/press-to-talk", {
        state: {
          matchFolderName,
          myTeam,
          opposition,
          userId,
          selectedTeamId,
          matchId,
          matchFolder,
          matchDate,
          status,
          otherInfo,
          homeAway,
          initialTeamScores,
          playerSelections: filteredPlayerSelections, // pass the filtered array here
          playersWithIds,
          matchLength,
          numberOfParts,
          matchType,
          formation,
          selectedFormation: selectedFormationObj,
          formationPositions,
          formationCoordinates,
          markerColor,
          gkColor,
          positionAssignments,
        },
      });
    } catch (error) {
      console.error("Error saving match info to S3:", error);
      alert("Failed to save match information. Please try again.");
    }
  };

  const saveMatchInfoToS3 = async (folderName, data) => {
    const fileName = `${folderName}/match-info.json`;
    const url = `https://${config.BucketName}.s3.${config.region}.amazonaws.com/${fileName}`;

    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error("Error saving match info to S3");
      }
      devLog("Match info saved to S3");
    } catch (error) {
      console.error("Error uploading match info:", error);
    }
  };

  return (
    <div className="mlf-container">
      {/* Progress Bar */}
      <div className="progress-container">
        <div className="progress-bar">
          {/* Step 1 */}
          <div className={`progress-step ${true ? "active" : ""}`}>
            <div className="circle">&#10003;</div>
            <span className="label">Match Setup</span>
          </div>

          {/* Line */}
          <div className="progress-line"></div>

          {/* Step 2 */}
          <div className={`progress-step ${true ? "active" : ""}`}>
            <div className="circle">2</div>
            <span className="label">Team Selection</span>
          </div>

          {/* Line */}
          <div className="progress-line"></div>

          {/* Step 3 */}
          <div className={`progress-step ${false ? "active" : ""}`}>
            <div className="circle">3</div>
            <span className="label">Start Game</span>
          </div>
        </div>
      </div>
      <h1 className="mlf-title">Team Selection</h1>

      {/* Toggle Preferences Button */}
      <button
        className="toggle-preferences-button"
        onClick={() => setPreferencesVisible(!preferencesVisible)}
      >
        {preferencesVisible
          ? "Hide Match Preferences"
          : "Show Match Preferences"}
      </button>

      {/* Conditionally Render Preferences */}
      {preferencesVisible && (
        <div>
          {/* Top Dropdowns */}
          <div className="mlf-top-dropdowns">
            {/* Match Type Dropdown */}
            <div className="mlf-dropdown-group">
              <label htmlFor="matchType" className="mlf-dropdown-label">
                Match Type
              </label>
              <select
                id="matchType"
                value={matchType}
                onChange={handleMatchTypeChange}
                className="mlf-dropdown"
              >
                {matchTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}v{type}
                  </option>
                ))}
              </select>
            </div>

            {/* Match Length Dropdown */}
            <div className="mlf-dropdown-group">
              <label htmlFor="matchLength" className="mlf-dropdown-label">
                Part Length
              </label>
              <select
                id="matchLength"
                value={matchLength}
                onChange={handleMatchLengthChange}
                className="mlf-dropdown"
              >
                {matchLengths.map((length) => (
                  <option key={length} value={length}>
                    {length} mins
                  </option>
                ))}
              </select>
            </div>

            {/* Number of Parts Dropdown */}
            <div className="mlf-dropdown-group">
              <label htmlFor="numberOfParts" className="mlf-dropdown-label">
                Parts
              </label>
              <select
                id="numberOfParts"
                value={numberOfParts}
                onChange={handleNumberOfPartsChange}
                className="mlf-dropdown"
              >
                <option value="2">2 Halves</option>
                <option value="4">4 Quarters</option>
              </select>
            </div>
          </div>

          {/* Color Selector */}
          <div className="mlf-color-selectors">
            {/* Player Marker Color Selector */}
            <div className="mlf-dropdown-group">
              <label htmlFor="markerColor" className="mlf-dropdown-label">
                Player Marker Color
              </label>
              <select
                id="markerColor"
                value={markerColor}
                onChange={handleMarkerColorChange}
                className="mlf-dropdown"
              >
                {colorOptions.map((color) => (
                  <option key={color.value} value={color.value}>
                    {color.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Formation Selection */}
      {matchType && (
        <div className="mlf-formation-section">
          <label htmlFor="formation" className="mlf-section-title">
            Select Formation
          </label>
          <div className="mlf-formation-buttons">
            {formationsByMatchType[matchType]?.map((f, index) => (
              <button
                key={index}
                className={`mlf-formation-button ${
                  formation === f.name ? "active" : ""
                }`}
                onClick={() => handleFormationChange(f.name)}
              >
                {f.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Pitch Visualization */}
      <div className="mlf-formation-section">
        <label htmlFor="formation" className="mlf-section-title">
          Tap a player icon to assign to a position
        </label>
      </div>
      <div
        style={{
          margin: "0 auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          overflow: "hidden",
        }}
      >
        {formation &&
          selectedFormationObj &&
          formationCoordinates.length > 0 && (
            <Pitch
              formationPositions={formationPositions}
              positionAssignments={positionAssignments}
              players={playersWithIds}
              coordinates={formationCoordinates}
              markerColor={markerColor}
              gkColor={gkColor}
              isPressToTalk={false}
              isFormationPage={true} // IMPORTANT for controlling the click behavior
              onPositionAssignmentChange={handlePlayerAssignment}
            />
          )}
      </div>

      {/* Position Assignments (Dropdown method) */}
      {/* Button to toggle Position Assignments */}
      {/* {formation && (
        <div style={{ marginTop: "1rem" }}>
          <div style={{ marginTop: "1rem" }}>
            <button
              className="showAssignments"
              onClick={() => setShowAssignments(!showAssignments)}
            >
              {showAssignments ? "Hide" : "Show"} Position Assignments
            </button>
          </div>

          {showAssignments && (
            <div className="mlf-position-assignments">
              <h3 className="mlf-section-title">Assign Players to Positions</h3>
              <ul className="mlf-position-list">
                {formationPositions.map((position, index) => (
                  <li key={index} className="mlf-position-item">
                    <label className="mlf-position-label">
                      {position}:
                      <select
                        className="mlf-position-player-dropdown"
                        value={positionAssignments[index] || ""}
                        onChange={(e) =>
                          handlePlayerAssignment(index, e.target.value)
                        }
                      >
                        <option value="">-- Select Player --</option>
                        {playersWithIds
                          .filter((player) => {
                            // Exclude players assigned elsewhere
                            const assignedElsewhere =
                              assignedPlayers.includes(player.player_id) &&
                              positionAssignments[index] !== player.player_id;
                            return !assignedElsewhere;
                          })
                          .map((player) => (
                            <option
                              key={player.player_id}
                              value={player.player_id}
                            >
                              {player.player_name}
                            </option>
                          ))}
                      </select>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )} */}

      {/* Unassigned Players */}
      <div className="mlf-unassigned-players">
        <h3 className="mlf-section-title">Subs / Not Selected</h3>
        <ul className="mlf-player-list">
          {playersWithIds
            .filter((player) => !assignedPlayers.includes(player.player_id))
            .map((player) => (
              <li key={player.player_id} className="mlf-player-item">
                <div className="mlf-player-info">
                  <span className="mlf-player-name">{player.player_name}</span>
                  {/* Status Buttons */}
                  <div className="mlf-button-group">
                    <button
                      onClick={() =>
                        handleStatusChange(player.player_id, "bench")
                      }
                      className={`mlf-button ${
                        playerStatuses[player.player_id]?.status === "bench"
                          ? "mlf-active"
                          : "mlf-inactive"
                      }`}
                      aria-label={`Set ${player.player_name} as Sub`}
                    >
                      Sub
                    </button>
                    <button
                      onClick={() =>
                        handleStatusChange(player.player_id, "home")
                      }
                      className={`mlf-button ${
                        playerStatuses[player.player_id]?.status === "home"
                          ? "mlf-active"
                          : "mlf-inactive"
                      }`}
                      aria-label={`Set ${player.player_name} as Not Selected`}
                    >
                      Not Selected
                    </button>
                  </div>
                </div>
              </li>
            ))}
        </ul>
      </div>

      {/* Validation Message */}
      {!isValid && (
        <p className="mlf-validation-message">
          Please select a formation and assign all positions.
        </p>
      )}

      {/* Continue Button */}
      <button
        onClick={handleContinue}
        className="mlf-continue-button"
        disabled={!isValid}
      >
        Continue
      </button>
    </div>
  );
};

export default MatchLengthAndFormation;
